/** localStorage key holding the signed-in account's avatar initials. */
export const ACCOUNT_INITIALS_KEY = "remnutri_account_initials";

/** localStorage key holding the identifying fields from the login response. */
export const ACCOUNT_USER_KEY = "remnutri_user";

/**
 * Identity claims read out of the stored session token.
 *
 * `POST /users/getProfile` can fail with "Cannot read properties of undefined
 * (reading 'userId')" — a TypeError raised inside the handler, so it is reaching
 * for `userId` on something that is not there. The token itself is fine (a bad
 * one is refused with 400 "Invalid or expired token" and a missing one with 401,
 * both before the handler runs), which leaves two possibilities: it dereferences
 * the decoded token, which only the server can fix, or it dereferences the
 * request body — which this app was sending empty. Supplying the id covers the
 * second case and costs nothing if the handler ignores it.
 *
 * Only the payload segment is decoded, never the signature, and only to name the
 * user the request is already authenticated as.
 */
export const readTokenIdentity = (): { userId?: string } => {
  try {
    const payload = localStorage.getItem("remnutri_auth_token")?.split(".")[1];
    if (!payload) return {};
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    const userId = claims?.userId ?? claims?.user_id ?? claims?.id ?? claims?.sub;
    return userId ? { userId: String(userId) } : {};
  } catch {
    // A token that is not a JWT, or a payload that is not JSON, is not worth
    // failing a page over — the request still carries the Authorization header.
    return {};
  }
};

/**
 * The signed-in user's id and display name, from the two places they live.
 *
 * The id comes from the token's claims and the name from the login response
 * cached under ACCOUNT_USER_KEY — neither holds both. Used where a record
 * stores a user id and the only name available is the viewer's own.
 */
export const readSignedInUser = (): { id?: string; name?: string } => {
  const { userId } = readTokenIdentity();
  try {
    const stored = localStorage.getItem(ACCOUNT_USER_KEY);
    const parsed = stored ? JSON.parse(stored) : null;
    const name = (parsed?.user_name ?? "").toString().trim();
    return { id: userId, name: name || undefined };
  } catch {
    // Private browsing or blocked storage — the id alone is still useful.
    return { id: userId };
  }
};

/**
 * Two-letter initials from a first and last name, matching what the My Profile
 * card shows: "Ravva Venkata Sai" + "Likith" -> "RL".
 */
export const initialsFromNames = (first?: string | null, last?: string | null) => {
  const a = (first ?? "").trim().charAt(0);
  const b = (last ?? "").trim().charAt(0);
  return `${a}${b}`.toUpperCase();
};

/**
 * Two-letter initials from a single display name, used when only the login
 * response is available. Takes the first and last word so a middle name does not
 * displace the surname: "Ravva Venkata Sai Likith" -> "RL".
 */
export const initialsFromFullName = (name?: string | null) => {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return initialsFromNames(parts[0], parts[parts.length - 1]);
};

/** Reads the cached initials, so the avatar paints before any request lands. */
export const readCachedInitials = () => {
  try {
    return localStorage.getItem(ACCOUNT_INITIALS_KEY) ?? "";
  } catch {
    return "";
  }
};

/** Caches initials for the next page load. Ignores a full or blocked storage. */
export const cacheInitials = (initials: string) => {
  try {
    if (initials) localStorage.setItem(ACCOUNT_INITIALS_KEY, initials);
  } catch {
    /* not worth failing the page over */
  }
};

/** Called on login, logout and forced re-auth so one account never shows another's initials. */
export const clearCachedAccount = () => {
  try {
    localStorage.removeItem(ACCOUNT_INITIALS_KEY);
    localStorage.removeItem(ACCOUNT_USER_KEY);
  } catch {
    /* nothing to do */
  }
};

/** Pulls the profile object out of whichever envelope /users/* answers with. */
export const unwrapProfileBody = (body: any) =>
  body?.data ?? body?.profile ?? body;
